import { useEffect, useMemo, useState } from 'react'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { EmptyState } from '../../shared/ui/EmptyState'
import { toast } from '../../shared/ui/toastStore'
import { confirm } from '../../shared/ui/confirmStore'
import { useAuthStore } from '../auth/authStore'
import {
  getCatalogSegments,
  getCatalogCategories,
  getCatalogParts,
  getCatalogAllParts,
  searchCatalogParts,
  addCatalogPart,
  changeCatalogSegment,
  removeCatalogPart,
  type CatalogPart,
} from '../../shared/api/catalog.api'
import { CatalogPartsGrid } from './CatalogPartsGrid'

// Спец-значение левого списка: «Все сегменты» (весь каталог с пагинацией).
const ALL = '__ALL__'

// Раздел «Каталог»: слева сегменты (+ «Все сегменты»), справа три колонки —
// Артикул / Наименование / Сегмент. Источник — наложение справочника УНФ и
// обученных категорий. Правка (добавить / сменить сегмент / убрать оверрайд) —
// ADMIN и ROP. Изменения влияют только на разбор НОВЫХ заявок.
export default function CatalogPage() {
  const user = useAuthStore((s) => s.user)
  const role = user?.role
  const canEdit = role === 'ADMIN' || role === 'ROP'
  const qc = useQueryClient()

  const [selected, setSelected] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [addPart, setAddPart] = useState('')
  const [addCategory, setAddCategory] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  const isSearching = debouncedQuery.length >= 1

  const segmentsQ = useQuery({ queryKey: ['catalog', 'segments'], queryFn: getCatalogSegments })
  const categoriesQ = useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: getCatalogCategories,
  })

  // По умолчанию открываем «Все сегменты» — видно весь каталог сразу.
  useEffect(() => {
    if (selected === null && segmentsQ.data) setSelected(ALL)
  }, [segmentsQ.data, selected])

  const partsQ = useInfiniteQuery({
    queryKey: ['catalog', 'parts', selected],
    queryFn: ({ pageParam }) =>
      selected === ALL ? getCatalogAllParts(pageParam) : getCatalogParts(selected!, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    enabled: !!selected && !isSearching,
  })

  const searchQ = useQuery({
    queryKey: ['catalog', 'search', debouncedQuery],
    queryFn: () => searchCatalogParts(debouncedQuery),
    enabled: isSearching,
  })

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['catalog'] })

  const addMut = useMutation({
    mutationFn: (vars: { partNumber: string; category: string }) =>
      addCatalogPart(vars.partNumber, vars.category),
    onSuccess: (part) => {
      invalidate()
      setQuery('')
      setSelected(part.category)
      setAddPart('')
      setAddCategory('')
      setAddOpen(false)
      toast.success(`Партномер добавлен в сегмент «${part.category}»`)
    },
    onError: (e: unknown) => toast.error(errText(e)),
  })

  const changeMut = useMutation({
    mutationFn: (vars: { artKey: string; category: string; partNumber: string }) =>
      changeCatalogSegment(vars.artKey, vars.category, vars.partNumber),
    onSuccess: (part) => {
      invalidate()
      toast.success(`Сегмент изменён на «${part.category}»`)
    },
    onError: (e: unknown) => toast.error(errText(e)),
  })

  const removeMut = useMutation({
    mutationFn: (artKey: string) => removeCatalogPart(artKey),
    onSuccess: () => {
      invalidate()
      toast.success('Оверрайд убран — парт вернулся к категории справочника')
    },
    onError: (e: unknown) => toast.error(errText(e)),
  })

  const handleAdd = () => {
    const pn = addPart.trim()
    const cat = (addCategory || (selected && selected !== ALL ? selected : '') || '').trim()
    if (!pn) return toast.error('Укажите партномер')
    if (!cat) return toast.error('Укажите сегмент')
    addMut.mutate({ partNumber: pn, category: cat })
  }

  const handleRemove = async (part: CatalogPart) => {
    const ok = await confirm({
      title: 'Убрать оверрайд?',
      message: `Ручная категория партномера «${part.article}» будет убрана. Если парт есть в справочнике УНФ — вернётся к его категории. Старые заявки не изменятся.`,
      danger: true,
      confirmText: 'Убрать',
    })
    if (ok) removeMut.mutate(part.artKey)
  }

  const segments = segmentsQ.data?.segments ?? []
  const total = segmentsQ.data?.total ?? 0
  const categories = categoriesQ.data ?? []

  const visibleSegments = useMemo(() => {
    if (!isSearching) return segments
    const ql = debouncedQuery.toLowerCase()
    return segments.filter((s) => s.category.toLowerCase().includes(ql))
  }, [segments, isSearching, debouncedQuery])

  // Источник правых строк: результаты поиска или страницы выбранного сегмента.
  const parts: CatalogPart[] = isSearching
    ? (searchQ.data ?? [])
    : (partsQ.data?.pages.flatMap((p) => p.items) ?? [])
  const loadedTotal = isSearching ? parts.length : (partsQ.data?.pages[0]?.total ?? 0)

  const rightBusy = isSearching ? searchQ.isLoading : partsQ.isLoading
  const rightError = isSearching ? searchQ.error : partsQ.error
  const isRightError = isSearching ? searchQ.isError : partsQ.isError

  if (segmentsQ.isLoading) return <PageLoader />
  if (segmentsQ.isError) return <ErrorMessage error={segmentsQ.error} />

  const inputCls =
    'px-2.5 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900'

  const headerTitle = isSearching
    ? 'Результаты поиска'
    : selected === ALL
      ? 'Все сегменты'
      : (selected ?? 'Выберите сегмент')

  return (
    <div className="p-4 md:p-6">
      <div className="mb-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Каталог
          <span className="ml-2 text-sm font-normal text-gray-400">
            всего партномеров: {total.toLocaleString('ru-RU')}
          </span>
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Артикул · наименование · сегмент. Наложение справочника УНФ и ручных категорий. Правки
          применяются к новым заявкам — наименование в уже созданных позициях не меняется.
        </p>
      </div>

      {/* Глобальный поиск — по артикулу, наименованию и сегменту */}
      <div className="mb-3 relative max-w-xl">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по артикулу, наименованию или сегменту…"
          className={`${inputCls} w-full pr-8`}
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Очистить"
          >
            ✕
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* Сегменты */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 p-2 flex flex-col">
          <div className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-gray-400 shrink-0">
            Сегменты ({visibleSegments.length})
          </div>
          <ul className="space-y-0.5 overflow-y-auto max-h-[calc(100vh-280px)]">
            {/* «Все сегменты» — всегда первым, вне поиска */}
            {!isSearching && (
              <li>
                <button
                  onClick={() => setSelected(ALL)}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-sm text-left transition-colors ${
                    selected === ALL
                      ? 'bg-brand-50 text-brand-700 font-medium'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                  }`}
                >
                  <span className="truncate font-medium">Все сегменты</span>
                  <span className="ml-2 shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-brand-100 text-brand-700">
                    {total.toLocaleString('ru-RU')}
                  </span>
                </button>
              </li>
            )}
            {visibleSegments.length === 0 ? (
              <li className="px-2 py-3 text-sm text-gray-400">
                {isSearching ? 'Сегмент по запросу не найден' : 'Пока пусто'}
              </li>
            ) : (
              visibleSegments.map((s) => (
                <li key={s.category}>
                  <button
                    onClick={() => {
                      setQuery('')
                      setSelected(s.category)
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-sm text-left transition-colors ${
                      !isSearching && selected === s.category
                        ? 'bg-brand-50 text-brand-700 font-medium'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    <span className="truncate">{s.category}</span>
                    <span className="ml-2 shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {s.count.toLocaleString('ru-RU')}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Партномера */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3 shrink-0">
            <h2 className="text-base font-medium text-gray-900 dark:text-gray-100">
              {headerTitle}
              {(isSearching || selected) && (
                <span className="ml-2 text-sm text-gray-400">
                  {isSearching
                    ? `${parts.length} шт`
                    : `${parts.length} из ${loadedTotal.toLocaleString('ru-RU')}`}
                </span>
              )}
            </h2>
            {canEdit && (
              <button
                onClick={() => setAddOpen((v) => !v)}
                className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 shrink-0"
              >
                + Добавить парт
              </button>
            )}
          </div>

          {/* Форма добавления */}
          {canEdit && addOpen && (
            <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3 flex flex-col sm:flex-row gap-2 sm:items-end shrink-0">
              <label className="flex-1 text-xs text-gray-500">
                Партномер
                <input
                  value={addPart}
                  onChange={(e) => setAddPart(e.target.value)}
                  placeholder="Напр. B82442A1152-K"
                  className={`mt-1 w-full ${inputCls}`}
                />
              </label>
              <label className="flex-1 text-xs text-gray-500">
                Сегмент
                <input
                  list="catalog-categories"
                  value={addCategory}
                  onChange={(e) => setAddCategory(e.target.value)}
                  placeholder={selected && selected !== ALL ? selected : 'Напр. Конденсатор'}
                  className={`mt-1 w-full ${inputCls}`}
                />
              </label>
              <button
                onClick={handleAdd}
                disabled={addMut.isPending}
                className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
              >
                {addMut.isPending ? 'Добавляю…' : 'Добавить'}
              </button>
            </div>
          )}

          <datalist id="catalog-categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>

          {!isSearching && !selected ? (
            <EmptyState title="Сегмент не выбран" description="Выберите сегмент слева." />
          ) : rightBusy ? (
            <PageLoader />
          ) : isRightError ? (
            <ErrorMessage error={rightError} />
          ) : parts.length === 0 ? (
            <EmptyState
              title={isSearching ? 'Ничего не найдено' : 'В сегменте нет партномеров'}
              description={isSearching ? 'Измените запрос.' : undefined}
            />
          ) : (
            <CatalogPartsGrid
              parts={parts}
              canEdit={canEdit}
              userId={user?.id}
              onChangeSegment={(artKey, category, partNumber) =>
                changeMut.mutate({ artKey, category, partNumber })
              }
              onRemove={handleRemove}
              hasNextPage={!isSearching && partsQ.hasNextPage}
              isFetchingMore={partsQ.isFetchingNextPage}
              onLoadMore={() => void partsQ.fetchNextPage()}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function errText(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
  return err?.response?.data?.error?.message || err?.message || 'Ошибка'
}
